import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {classes, style} from './doppe-viewer-video-player.st.css';
import {
    doppeValidateYoutubeShortURL,
    evaluateFunction,
    EventEmitter,
    urlCreateQueryString,
    VideoServices,
    videoServicesResolveVideoSource
} from '@wix/devzai-utils-common';
import {
    AnimatedAutoSizedElement,
    RatioMaintainer,
    useDynamicRef,
    useDynamicRefCurrentInstance,
    useRootWindow,
    VideoPlayer,
    VideoPlayerPreload
} from '@wix/devzai-utils-react';
import {DoppeBymoPageVideoSource} from '../../client-server-common';
import {WixVideoResourcePlayer} from '@wix/devzai-common-client';
import {DoppeViewerVideoPlayerOverlay} from '../doppe-viewer-video-player-overlay/doppe-viewer-video-player-overlay';
import {doppeClientHttpClientCreateIsomorphic} from '../../doppe-client-http-client/doppe-client-http-client';
import {domLoadScript} from '@wix/devzai-utils-dom';

export const DoppeViewerVideoPlayer = React.memo(function DoppeViewerVideoPlayer (props: DoppeViewerVideoPlayer.Props) {
    const {
        videoResource,
        onStartPlaying,
        muteVideo,
        autoplay,
        videoTitle,
        className,
        ...htmlAttributes
    } = props;

    const wixVideoPlayerPoster = useCallback((renderProps: WixVideoResourcePlayer.OverlayRenderProps) => {
        return (
            <DoppeViewerVideoPlayerOverlay
                overlayRenderProps={renderProps}
                widthSpec={{contentRelativeWidth: 1}}
                layoutSpec={{widthHeightRatio: 16 / 9}}
            />
        )
    }, [])

    const videoPlayerRef = useDynamicRef<VideoPlayer>()
    const videoPlayer = useDynamicRefCurrentInstance(videoPlayerRef);

    useEffect(() => {

        if (videoPlayer && onStartPlaying) {
            const startedPlayingBinding = EventEmitter.one(videoPlayer.eventStartedPlaying, () => {
                onStartPlaying?.();
            })

            return () => {
                startedPlayingBinding.dispose();
            }
        }

        return undefined;

    }, [videoPlayer, onStartPlaying]);

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
        >
            {typeof videoResource === 'string' ? (
                <ExternalVideoPlayer
                    videoUrl={videoResource}
                    muteVideo={muteVideo}
                    autoplay={autoplay}
                    videoTitle={videoTitle}
                />
            ) : evaluateFunction(() => {
                return (
                    <RatioMaintainer
                        widthHeightRatio={16 / 9}
                    >
                        {
                            videoResource !== null ?
                                <WixVideoResourcePlayer
                                    wixVideoResource={videoResource}
                                    autoplay={autoplay}
                                    muted={muteVideo}
                                    httpClient={doppeClientHttpClientCreateIsomorphic()}
                                    preload={VideoPlayerPreload.None}
                                    poster={wixVideoPlayerPoster}
                                    videoPlayerRef={videoPlayerRef}
                                />
                                :
                                null
                        }
                    </RatioMaintainer>
                )
            })}
        </div>
    );
});

export namespace DoppeViewerVideoPlayer {

    export interface Props extends React.HTMLAttributes<any> {
        videoResource: DoppeBymoPageVideoSource | null;
        onStartPlaying?: () => void;
        muteVideo: boolean;
        autoplay: boolean;
        videoTitle?: string;
    }
}

const ExternalVideoPlayer = React.memo(function ExternalVideoPlayer(
    props: {
        videoUrl: string;
        videoTitle?: string;
        muteVideo: boolean;
        autoplay: boolean;
    }
) {
    const {
        videoUrl,
        muteVideo,
        autoplay,
        videoTitle
    } = props;

    const rootWindow = useRootWindow();
    const actionTitle = videoTitle;

    const videoServiceSource = useMemo(() => {
        return videoServicesResolveVideoSource(videoUrl);
    }, [videoUrl])

    useEffect(() => {
        if (videoServiceSource?.videoService === VideoServices.TikTok) {
            domLoadScript('https://www.tiktok.com/embed.js', {rootWindow: rootWindow}).catch(() => {
            });
        }
    }, [videoServiceSource])

    const [hostname, setHostname] = useState<string>();
    useLayoutEffect(() => {
        setHostname(window.location.hostname)
    }, [])

    if (videoServiceSource) {
        switch (videoServiceSource.videoService) {
            case VideoServices.Youtube: {

                const videoId = videoServiceSource.videoId;

                const isYoutubeShort = doppeValidateYoutubeShortURL(videoUrl);
                const ratio = isYoutubeShort ? (453 / 804) : (16 / 9);

                return (
                    <RatioMaintainer
                        widthHeightRatio={ratio}
                    >
                        <iframe
                            className={style(classes.iframe, {})}
                            src={`https://www.youtube.com/embed/${videoId}?${urlCreateQueryString({
                                autoplay: autoplay ? '1' : '0',
                                mute: muteVideo ? '1' : '0',
                                playsinline: '1',
                                modestbranding: '1',
                                origin: hostname,
                                iv_load_policy: '3',
                                rel: '0',
                                start: '0',
                                // cc_load_policy: '0',
                                // fs: '0'
                            }, false)}`}
                            allow={`fullscreen; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
                            frameBorder="0"
                            title={actionTitle}
                        />
                    </RatioMaintainer>
                )
            }
            case VideoServices.YoutubePlaylist: {

                const videoId = videoServiceSource.videoId;

                return (
                    <RatioMaintainer
                        widthHeightRatio={1}
                    >
                        <iframe
                            src={`https://www.youtube.com/embed/videoseries?${urlCreateQueryString({
                                list: videoId,
                                autoplay: autoplay ? '1' : '0',
                                mute: muteVideo ? '1' : '0',
                                playsinline: '1',
                                modestbranding: '1',
                                origin: hostname,
                                iv_load_policy: '3',
                                rel: '0',
                                start: '0',
                                // cc_load_policy: '0',
                                // fs: '0'
                            }, false)}`}
                            title={actionTitle}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen>
                        </iframe>
                    </RatioMaintainer>
                )
            }
            case VideoServices.Vimeo: {

                const videoId = videoServiceSource.videoId;

                return (
                    <RatioMaintainer
                        widthHeightRatio={16 / 9}
                    >
                        <iframe
                            src={`https://player.vimeo.com/video/${videoId}?${urlCreateQueryString({
                                autoplay: autoplay ? '1' : '0',
                                muted: muteVideo ? '1' : '0',
                                playsinline: '1',
                                portrait: '0',
                                title: '0',
                                byline: '0',
                                autopause: '0'
                            }, false)}`}
                            allow={`accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
                            frameBorder="0"
                            title={actionTitle}
                        />
                    </RatioMaintainer>
                )
            }
            case VideoServices.TikTok: {
                return (
                    <AnimatedAutoSizedElement
                        durationMilliseconds={400}
                    >
                        <blockquote
                            className={style(classes.tiktokEmbed, 'tiktok-embed')}
                            cite={`https://www.tiktok.com/${videoServiceSource.user}/video/${videoServiceSource.videoId}`}
                            data-video-id={videoServiceSource.videoId}
                        >
                            <section/>
                        </blockquote>
                    </AnimatedAutoSizedElement>

                )
            }
        }
    }

    return (
        <RatioMaintainer
            widthHeightRatio={16 / 9}
        >
            <div className={style(classes.notSupportedVideo, {})}>Not Supported Video</div>
        </RatioMaintainer>
    )
});
